import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  listAccompanyShoppingProviderApplications,
  getAccompanyShoppingProviderApplicationById,
  updateProviderApplicationsStatus,
  reviewProviderApplication,
  updateAccompanyShoppingProviderApplication
} from '../../api/accompanyShoppingProviderApplication';

// 申请状态映射
export const APPLICATION_STATUS_MAP = {
  0: '待审核',
  1: '已通过',
  2: '已拒绝',
  3: '已取消'
};

// 分页查询陪逛服务提供者申请的异步 thunk
export const fetchAccompanyShoppingProviderApplications = createAsyncThunk(
  'accompanyShoppingProviderApplication/fetchApplications',
  async (params, { rejectWithValue }) => {
    try {
      const response = await listAccompanyShoppingProviderApplications(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 获取单个陪逛服务提供者申请详情的异步 thunk
export const fetchAccompanyShoppingProviderApplicationDetail = createAsyncThunk(
  'accompanyShoppingProviderApplication/fetchApplicationDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAccompanyShoppingProviderApplicationById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 批量更新陪逛服务提供者申请状态的异步 thunk
export const batchUpdateProviderApplicationsStatus = createAsyncThunk(
  'accompanyShoppingProviderApplication/batchUpdateStatus',
  async ({ applicationIds, status }, { rejectWithValue }) => {
    try {
      const response = await updateProviderApplicationsStatus(applicationIds, status);
      return { applicationIds, status, updatedCount: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 审核陪逛服务提供者申请的异步 thunk
export const reviewApplication = createAsyncThunk(
  'accompanyShoppingProviderApplication/reviewApplication',
  async ({ applicationId, status, reviewComments }, { rejectWithValue }) => {
    try {
      const response = await reviewProviderApplication({
        applicationId, 
        status, 
        reviewComments 
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// 更新陪逛服务提供者申请信息的异步 thunk
export const updateProviderApplication = createAsyncThunk(
  'accompanyShoppingProviderApplication/updateApplication',
  async (applicationData, { rejectWithValue }) => {
    try {
      const response = await updateAccompanyShoppingProviderApplication(applicationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const accompanyShoppingProviderApplicationSlice = createSlice({
  name: 'accompanyShoppingProviderApplication',
  initialState: {
    applications: [],
    total: 0,
    current: 1,
    pageSize: 10,
    currentApplication: null,
    selectedApplicationIds: [],
    loading: false,
    error: null
  },
  reducers: {
    setSelectedApplicationIds: (state, action) => {
      state.selectedApplicationIds = action.payload;
    }
  },
  extraReducers: (builder) => {
    // 处理分页查询陪逛服务提供者申请的 reducer
    builder.addCase(fetchAccompanyShoppingProviderApplications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAccompanyShoppingProviderApplications.fulfilled, (state, action) => {
      state.loading = false;
      state.applications = action.payload.records || [];
      state.total = action.payload.total || 0;
      state.current = action.payload.current || 1;
      state.pageSize = action.payload.size || 10;
    });
    builder.addCase(fetchAccompanyShoppingProviderApplications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理获取申请详情的 reducer
    builder.addCase(fetchAccompanyShoppingProviderApplicationDetail.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAccompanyShoppingProviderApplicationDetail.fulfilled, (state, action) => {
      state.loading = false;
      state.currentApplication = action.payload;
    });
    builder.addCase(fetchAccompanyShoppingProviderApplicationDetail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理批量更新申请状态的 reducer
    builder.addCase(batchUpdateProviderApplicationsStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(batchUpdateProviderApplicationsStatus.fulfilled, (state, action) => {
      state.loading = false;
      const { applicationIds, status } = action.payload;
      
      // 更新列表中的申请状态
      state.applications = state.applications.map(app => 
        applicationIds.includes(app.id) ? { ...app, applicationStatus: status } : app
      );

      // 如果当前申请在更新列表中，也更新其状态
      if (state.currentApplication && applicationIds.includes(state.currentApplication.id)) {
        state.currentApplication.applicationStatus = status;
      }

      // 清空选中的申请ID
      state.selectedApplicationIds = [];
    });
    builder.addCase(batchUpdateProviderApplicationsStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理审核申请的 reducer
    builder.addCase(reviewApplication.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(reviewApplication.fulfilled, (state, action) => {
      state.loading = false;
      
      // 更新列表中的申请
      const index = state.applications.findIndex(app => app.id === action.payload.id);
      if (index !== -1) {
        state.applications[index] = action.payload;
      }

      // 更新当前申请
      if (state.currentApplication && state.currentApplication.id === action.payload.id) {
        state.currentApplication = action.payload;
      }

      // 清空选中的申请ID
      state.selectedApplicationIds = [];
    });
    builder.addCase(reviewApplication.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 处理更新申请信息的 reducer
    builder.addCase(updateProviderApplication.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateProviderApplication.fulfilled, (state, action) => {
      state.loading = false;
      
      // 更新列表中的申请
      const index = state.applications.findIndex(app => app.id === action.payload.id);
      if (index !== -1) {
        state.applications[index] = action.payload;
      }

      // 更新当前申请
      if (state.currentApplication && state.currentApplication.id === action.payload.id) {
        state.currentApplication = action.payload;
      }
    });
    builder.addCase(updateProviderApplication.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  }
});

export const { setSelectedApplicationIds } = accompanyShoppingProviderApplicationSlice.actions;
export default accompanyShoppingProviderApplicationSlice.reducer;
